import { generateStableTrackId, getParticipantIdentity } from '../components/Player/utils/trackIdentifiers';

/**
 * Class representing a media track in the player.
 * 
 * Uses stable track identifiers that persist across participant reconnections.
 * This eliminates the need for complex track replacement logic.
 * 
 * @property {string} id - Stable identifier that persists across reconnections
 * @property {string} originalId - Original track ID from HLS or WebRTC
 * @property {string} hlsTrackId - Track ID from HLS recording (if available)
 * @property {string} webrtcTrackId - Current WebRTC track ID (if available)
 * @property {Array} trackIdHistory - History of all track IDs for this logical stream
 * @property {string} name - Human-readable name
 * @property {string} kind - Track type (e.g., "audio", "video")
 * @property {string} participantType - Role of participant (e.g., "proctor", "student")
 * @property {string} participantIdentity - Stable participant identity
 * @property {Object} webrtcRef - Reference to WebRTC media element or track
 * @property {Object} hls - HLS streaming config or instance
 * @property {string} status - Current track status (e.g., "active", "paused")
 */
export default class SessionTrack {
  constructor({
    id = '',
    name = '',
    kind = '',
    participantType = '',
    participantSid = '',
    webrtcRef = null,
    hls = null,
    status = '',
    source = '',
  } = {}) {
    // Store original ID for reference
    this.originalId = id;
    
    // Generate stable ID (will be set properly in populate methods)
    this.id = id;
    
    // Track both HLS and WebRTC IDs
    this.hlsTrackId = hls ? id : null;
    this.webrtcTrackId = webrtcRef ? id : null;
    
    // Track version history
    this.trackIdHistory = [{
      trackId: id,
      type: hls ? 'hls' : (webrtcRef ? 'webrtc' : 'unknown'),
      connectedAt: Date.now(),
      disconnectedAt: null
    }];
    
    // Track properties
    this.name = name;
    this.kind = kind;
    this.source = source;
    this.participantType = participantType;
    this.participantSid = participantSid;
    this.participantIdentity = null; // Will be set in populate methods
    
    // Video stream references
    this.webrtcRef = webrtcRef;
    this.hls = hls;
    this.status = status;
  }

  /**
   * Populate an existing Track instance from a LiveKit publication.
   * @param {SessionTrack} trackInstance - existing Track to populate
   * @param {{participant: object, publication: object}} trackRef
   * @returns {SessionTrack}
   */
  populateFromLiveKit(trackRef) {
    const { participant, publication } = trackRef;
    const originalId = publication.trackSid;
    const name = publication.trackName || publication.trackSid;
    const kind = publication.kind;
    const source = publication.source;
    
    // Set track properties
    this.originalId = originalId;
    this.name = name;
    this.kind = kind;
    this.source = source;
    this.participantType = participant?.participantInfo?.attributes?.type;
    this.participantSid = participant?.participantInfo?.attributes?.participantSid;
    this.participantIdentity = participant?.identity;
    this.webrtcRef = trackRef;
    this.webrtcTrackId = originalId;
    
    // Generate stable ID
    this.id = generateStableTrackId(this);
    
    // Initialize track history
    this.trackIdHistory = [{
      trackId: originalId,
      type: 'webrtc',
      connectedAt: Date.now(),
      disconnectedAt: null
    }];
    
    return this;
  }

  /**
   * Populate an existing Track instance from a HLS track.
   * @param {{participant: object, track: object}} trackObj
   * @returns {SessionTrack}
   */
  populateFromHls(trackObj) {
    const { participant, track } = trackObj;
    const originalId = track.trackId;
    const name = track?.info?.name || track.trackId;
    const kind = track.kind;
    const source = track.source;
    
    // Set track properties
    this.originalId = originalId;
    this.name = name;
    this.kind = kind;
    this.source = source;
    this.participantType = participant?.type;
    this.participantSid = participant?.sid;
    this.participantIdentity = participant?.identity;
    this.hls = trackObj;
    this.hlsTrackId = originalId;
    
    // Add trackId to statuses and positions if they don't already have it
    if (this.hls.track) {
      if (this.hls.track.statuses) {
        // Add initial not-yet-connected status at time 0 if track doesn't start at the beginning
        // This prevents gaps in the status timeline
        // Use null trackId to indicate this is a stable track status, not specific to any TR_ ID
        if (this.hls.track.statuses.length > 0) {
          const firstStatus = this.hls.track.statuses[0];
          if (firstStatus.sessionChangeTime > 0) {
            this.hls.track.statuses.unshift({
              status: 'not-yet-connected',
              sessionChangeTime: 0,
              changeDate: new Date(0).toISOString(),
              trackId: null
            });
          }
        }
        
        // Add trackId to statuses if not already present, but preserve null trackId
        // Also adjust online status times by +2 seconds to account for video stream buffering
        this.hls.track.statuses = this.hls.track.statuses.map(status => ({
          ...status,
          trackId: status.trackId !== undefined ? status.trackId : originalId,
          sessionChangeTime: status.status === 'online' 
            ? status.sessionChangeTime + 2 
            : status.sessionChangeTime
        }));
      }
      if (this.hls.track.positions) {
        this.hls.track.positions = this.hls.track.positions.map(pos => ({
          ...pos,
          trackId: pos.trackId || originalId
        }));
      }
    }
    
    // Generate stable ID
    this.id = generateStableTrackId(this);
    
    // Initialize track history
    this.trackIdHistory = [{
      trackId: originalId,
      type: 'hls',
      connectedAt: Date.now(),
      disconnectedAt: null
    }];
    
    return this;
  }

  /**
   * Updates this track with new WebRTC data (on reconnection).
   * 
   * When a participant reconnects after a disconnection, LiveKit creates new track objects
   * with different SIDs. This method updates the existing SessionTrack with the new LiveKit
   * track reference while maintaining the stable track ID.
   * 
   * The `lastUpdated` timestamp is set to trigger React component re-renders. Video components
   * use this timestamp in their React keys to force remounting when tracks reconnect, ensuring
   * video elements properly re-attach to the new LiveKit tracks.
   * 
   * @param {Object} newWebRTCTrack - New WebRTC track data
   * @returns {SessionTrack} This instance for chaining
   */
  updateWebRTCTrack(newWebRTCTrack) {
    const newTrackId = newWebRTCTrack.originalId || newWebRTCTrack.id;
    const trackIdChanged = this.webrtcTrackId !== newTrackId;
    
    // Close out the previous track in history
    if (this.trackIdHistory.length > 0) {
      const lastEntry = this.trackIdHistory[this.trackIdHistory.length - 1];
      if (!lastEntry.disconnectedAt) {
        lastEntry.disconnectedAt = Date.now();
      }
    }
    
    // Add new track to history
    this.trackIdHistory.push({
      trackId: newTrackId,
      type: 'webrtc',
      connectedAt: Date.now(),
      disconnectedAt: null
    });
    
    // Update current WebRTC reference
    this.webrtcRef = newWebRTCTrack.webrtcRef;
    this.webrtcTrackId = newTrackId;
    
    // Only update timestamp when track SID actually changes (real reconnection)
    // This prevents unnecessary component remounts during normal operation
    if (trackIdChanged) {
      this.lastUpdated = Date.now();
    }
    
    return this;
  }

  /**
   * Updates this track with HLS data.
   * Merges status histories from both the existing and new track to preserve
   * all online/offline events across reconnections with different TR_ IDs.
   * 
   * @param {Object} hlsTrack - HLS track data
   * @returns {SessionTrack} This instance for chaining
   */
  updateHLSTrack(hlsTrack) {
    const newHls = hlsTrack.hls;
    const oldTrackId = this.hlsTrackId;
    const newTrackId = hlsTrack.originalId || hlsTrack.id;
    
    // If we have existing HLS data with statuses, merge them with new statuses
    if (this.hls && this.hls.track && this.hls.track.statuses && 
        newHls && newHls.track && newHls.track.statuses) {
      
      // Add trackId to existing statuses if not already present, but preserve null trackId
      const existingStatuses = (this.hls.track.statuses || []).map(status => ({
        ...status,
        trackId: status.trackId !== undefined ? status.trackId : oldTrackId
      }));
      
      // Add trackId to new statuses, but skip the initial not-yet-connected status if it exists
      // (it will already be in existingStatuses from the first track)
      // Also adjust online status times by +2 seconds to account for video stream buffering
      const newStatuses = (newHls.track.statuses || [])
        .filter(status => !(status.sessionChangeTime === 0 && status.status === 'not-yet-connected' && status.trackId === null))
        .map(status => ({
          ...status,
          trackId: status.trackId !== undefined ? status.trackId : newTrackId,
          sessionChangeTime: status.status === 'online' 
            ? status.sessionChangeTime + 2 
            : status.sessionChangeTime
        }));
      
      // Combine and sort by sessionChangeTime
      const mergedStatuses = [...existingStatuses, ...newStatuses];
      mergedStatuses.sort((a, b) => a.sessionChangeTime - b.sessionChangeTime);
      
      // Merge positions as well
      const existingPositions = (this.hls.track.positions || []).map(pos => ({
        ...pos,
        trackId: pos.trackId || oldTrackId
      }));
      
      const newPositions = (newHls.track.positions || []).map(pos => ({
        ...pos,
        trackId: pos.trackId || newTrackId
      }));
      
      const mergedPositions = [...existingPositions, ...newPositions];
      mergedPositions.sort((a, b) => a.sessionChangeTime - b.sessionChangeTime);
      
      // Update the HLS data with merged statuses and positions
      this.hls = newHls;
      this.hls.track.statuses = mergedStatuses;
      this.hls.track.positions = mergedPositions;
      /*
      console.log(`[SessionTrack] Merged HLS track data for ${this.id}`, {
        oldTrackId,
        newTrackId,
        existingStatusCount: existingStatuses.length,
        newStatusCount: newStatuses.length,
        mergedStatusCount: mergedStatuses.length,
        existingPositionCount: existingPositions.length,
        newPositionCount: newPositions.length,
        mergedPositionCount: mergedPositions.length
      });
      */
    } else {
      // No existing data to merge, just add trackId to new data
      if (newHls && newHls.track) {
        if (newHls.track.statuses) {
          newHls.track.statuses = newHls.track.statuses.map(status => ({
            ...status,
            trackId: status.trackId || newTrackId
          }));
        }
        if (newHls.track.positions) {
          newHls.track.positions = newHls.track.positions.map(pos => ({
            ...pos,
            trackId: pos.trackId || newTrackId
          }));
        }
      }
      this.hls = newHls;
    }
    
    this.hlsTrackId = newTrackId;
    
    return this;
  }

  /**
   * Gets the current active track ID (WebRTC if available, otherwise HLS).
   * 
   * @returns {string} Current track ID
   */
  getCurrentTrackId() {
    return this.webrtcTrackId || this.hlsTrackId || this.originalId;
  }

  /**
   * Checks if this track has live WebRTC data.
   * 
   * @returns {boolean} True if track has WebRTC reference
   */
  hasLiveData() {
    return !!this.webrtcRef;
  }

  /**
   * Checks if this track has recorded HLS data.
   * 
   * @returns {boolean} True if track has HLS data
   */
  hasRecordedData() {
    return !!this.hls;
  }
}
